import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { mockData } from '../data/mock';
import emailjs from '@emailjs/browser';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

const Contact = () => {
  const { t } = useThemeLanguage();

  return (
    <section className="py-20 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light mb-6">
            {t('contact.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-light mb-8">{t('contact.coordinates')}</h3>
              
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{t('contact.phone')}</h4>
                    <p className="text-gray-300">{mockData.company.phone}</p>
                    <p className="text-sm text-gray-400 mt-1">{t('contact.available247')}</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.36a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{t('contact.email')}</h4>
                    <p className="text-gray-300">{mockData.company.email}</p>
                    <p className="text-sm text-gray-400 mt-1">{t('contact.responseTime')}</p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{t('contact.serviceArea')}</h4>
                    <p className="text-sm text-gray-400 mt-1">{mockData.company.coverage}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Hours */}
            <div className="bg-white/5 p-6 rounded-2xl">
              <h4 className="font-medium mb-4">{t('contact.serviceHours')}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('contact.mondaySunday')}</span>
                  <span>{t('contact.allDay')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('contact.holidays')}</span>
                  <span>{t('contact.serviceMaintained')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('contact.reservations')}</span>
                  <span>{t('contact.onlineOrPhone')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Contact Form */}
          <Card className="bg-white">
            <CardContent className="p-8">
              <h3 className="text-2xl font-light text-gray-900 mb-6">{t('contact.quickContact')}</h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target;
                  const firstName = form.firstName.value;
                  const lastName = form.lastName.value;
                  const phone = form.phone.value;
                  const email = form.email.value;
                  const subject = form.subject.value;
                  const message = form.message.value;

                  if (!firstName || !lastName || !email || !message) {
                    alert("Veuillez remplir tous les champs obligatoires !");
                    return;
                  }

                  const phoneNumber = "33749777371";
                  const whatsappMessage = encodeURIComponent(
                    `*Nouvelle demande de contact*\n\n` +
                    `*Nom:* ${firstName} ${lastName}\n` +
                    `*Téléphone:* ${phone}\n` +
                    `*Email:* ${email}\n` +
                    `*Sujet:* ${subject || "Non précisé"}\n` +
                    `*Message:*\n${message}`
                  );
                  window.open(`https://wa.me/${phoneNumber}?text=${whatsappMessage}`, "_blank");

                  emailjs.send(
                    "REACT_APP_EMAILJS_SERVICE_ID",
                    "REACT_APP_EMAILJS_TEMPLATE_ID",
                    { firstName, lastName, phone, email, subject, message },
                    "REACT_APP_EMAILJS_PUBLIC_KEY"
                  ).then(
                    (response) => console.log("✅ Email envoyé", response.status, response.text),
                    (error) => console.error("❌ Erreur EmailJS", error)
                  );

                  form.reset();
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <input 
                    name="firstName" 
                    type="text" 
                    placeholder={t('contact.firstName')} 
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[0-9]/g, '');
                    }}
                  />
                  <input 
                    name="lastName" 
                    type="text" 
                    placeholder={t('contact.lastName')} 
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[0-9]/g, '');
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input 
                    name="phone" 
                    type="tel" 
                    placeholder={t('contact.phonePlaceholder')} 
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9+.,\s]/g, '');
                    }}
                    pattern="[+]?[0-9.,\s]*"
                  />
                  <input name="email" type="email" placeholder={t('contact.emailPlaceholder')} className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-gray-900 placeholder-gray-400" />
                </div>

                <select name="subject" className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-gray-900">
                  <option value="" className="text-gray-400">{t('contact.subjectPlaceholder')}</option>
                  <option value="reservation">{t('contact.subjects.reservation')}</option>
                  <option value="information">{t('contact.subjects.information')}</option>
                  <option value="devis">{t('contact.subjects.quote')}</option>
                  <option value="reclamation">{t('contact.subjects.complaint')}</option>
                  <option value="autre">{t('contact.subjects.other')}</option>
                </select>

                <textarea name="message" rows="4" placeholder={t('contact.messagePlaceholder')} className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none bg-white text-gray-900 placeholder-gray-400"></textarea>

                <Button type="submit" className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3">
                  {t('contact.sendMessage')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Social Links */}
        <div className="text-center mt-16 pt-12 border-t border-white/20">
          <div className="flex justify-center space-x-6">
            {/* Facebook */}
            <a href="https://www.facebook.com/people/Riviera-Signature-VTC/61580351294218/" className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors group">
              <svg className="w-6 h-6 text-white group-hover:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            
            {/* Instagram */}
            <a href="https://www.instagram.com/rivierasignaturevtc" className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors group">
              <svg className="w-6 h-6 text-white group-hover:text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C8.396 0 7.933.016 6.706.071 5.481.126 4.65.389 3.927.735 3.175 1.098 2.543 1.587 1.912 2.218.281 3.85.016 5.481.071 6.706.016 7.933 0 8.396 0 12.017c0 3.624.016 4.086.071 5.314.055 1.226.318 2.057.664 2.78.365.752.854 1.384 1.486 2.015.63.63 1.263 1.12 2.015 1.486.723.346 1.554.609 2.78.664 1.227.055 1.69.071 5.314.071 3.624 0 4.086-.016 5.314-.071 1.226-.055 2.057-.318 2.78-.664.752-.366 1.384-.855 2.015-1.486.63-.631 1.12-1.263 1.486-2.015.346-.723.609-1.554.664-2.78.055-1.228.071-1.69.071-5.314 0-3.621-.016-4.084-.071-5.31-.055-1.226-.318-2.057-.664-2.78-.365-.752-.854-1.384-1.486-2.015C16.896.281 16.264-.208 15.512-.574 14.789-.92 13.958-1.183 12.732-1.238 11.505-1.293 11.042-1.309 7.421-1.309l4.596 1.309zm0 1.621c3.563 0 3.993.016 5.4.071 1.3.059 2.006.273 2.476.456.622.242 1.066.532 1.533.999.466.466.756.91.999 1.533.183.47.397 1.176.456 2.476.055 1.408.071 1.837.071 5.4s-.016 3.993-.071 5.4c-.059 1.3-.273 2.006-.456 2.476-.242.622-.532 1.066-.999 1.533-.466.466-.91.756-1.533.999-.47.183-1.176.397-2.476.456-1.408.055-1.837.071-5.4.071s-3.993-.016-5.4-.071c-1.3-.059-2.006-.273-2.476-.456-.622-.242-1.066-.532-1.533-.999-.466-.466-.756-.91-.999-1.533-.183-.47-.397-1.176-.456-2.476-.055-1.408-.071-1.837-.071-5.4s.016-3.993.071-5.4c.059-1.3.273-2.006.456-2.476.242-.622.532-1.066.999-1.533.466-.466.91-.756 1.533-.999.47-.183 1.176-.397 2.476-.456 1.408-.055 1.837-.071 5.4-.071z"/>
                <path d="M12.017 15.33a3.314 3.314 0 1 1 0-6.628 3.314 3.314 0 0 1 0 6.628zM12.017 7.052a4.98 4.98 0 1 0 0 9.96 4.98 4.98 0 0 0 0-9.96zM18.685 6.756a1.164 1.164 0 1 1-2.328 0 1.164 1.164 0 0 1 2.328 0z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
